import React from 'react'
import { StripeProvider, Elements } from 'react-stripe-elements'
import { isObjectEmpty } from '@jarvis.oddle/core/tools/object'
import { useFormContext } from '@jarvis.oddle/core/components/Form'
import { loadStripeScript } from 'src/utils/payment'
import StripeCardElement from './StripeCardElement'
import StripeFPXElement from './StripeFPXElement'
import ElementWrapper from './ElementWrapper'

export const stripeElementTypes = {
  fpx: 'FPX'
}

const StripeElement = ({
  type,
  apiKey,
  onStripeReady,
  onStripeLoaded,
  title
}: {
  type: string
  apiKey: string
  title?: string
  onStripeReady: (stripe) => void
  onStripeLoaded: (stripe) => void
}): JSX.Element => {
  const [stripeObject, setStripeObject] = React.useState(null)
  const { setFieldValue, setFieldTouched, errors, touched } = useFormContext()
  const isError = errors.card && touched.card
  const [validCard, setValidCard] = React.useState(false)

  const Element = React.useMemo(() => {
    switch (type) {
      case stripeElementTypes.fpx:
        return StripeFPXElement
      default:
        return StripeCardElement
    }
  }, [type])

  const onCardChange = (card) => {
    setValidCard(card.complete)
    setFieldValue('card', card, true)
    setFieldTouched('card', true)
  }

  const onInitialize = React.useCallback(async () => {
    await loadStripeScript()
    const stripeObject = window.Stripe(apiKey)
    setStripeObject(stripeObject)
  }, [apiKey])

  React.useEffect(() => {
    if (isObjectEmpty(stripeObject)) {
      onInitialize()
    }
  }, [onInitialize, stripeObject])

  return (
    <StripeProvider stripe={stripeObject}>
      <Elements>
        <ElementWrapper
          type={type}
          onStripeLoaded={onStripeLoaded}>
          {Element
            ? <Element
              isError={isError}
              onCardChange={onCardChange}
              onStripeReady={onStripeReady}
              errorCard={errors.card}
              validCard={validCard}
              title={title} />
            : null}
        </ElementWrapper>
      </Elements>
    </StripeProvider>
  )
}

export default StripeElement
